import React, { Component, ErrorInfo, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

interface Props extends WithTranslation {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
    // React swallows the throw before window.onerror fires, so
    // forward to Sentry explicitly. Tagged 'app' to distinguish from
    // the per-route boundary's 'route' tag in the Sentry dashboard.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? '' } },
      tags: { boundary: 'app' },
    });
  }

  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#070b14',
            color: '#eaf0ff',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          <div role="alert" style={{ textAlign: 'center', maxWidth: 440, padding: 40 }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'rgba(255,107,107,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: 28,
              }}
            >
              !
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 12px' }}>{t('error.title')}</h1>
            <p style={{ color: '#9fb0d7', fontSize: 14, lineHeight: 1.6, margin: '0 0 28px' }}>
              {t('error.description')}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 32px',
                background: 'linear-gradient(135deg, #2ee59d, #20c997)',
                border: 'none',
                borderRadius: 10,
                color: '#070b14',
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: 1,
                cursor: 'pointer',
                textTransform: 'uppercase',
              }}
            >
              {t('error.reloadPage')}
            </button>
            {this.state.error && (
              <pre
                style={{
                  marginTop: 24,
                  padding: 16,
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 8,
                  fontSize: 11,
                  color: '#ff6b6b',
                  textAlign: 'left',
                  overflow: 'auto',
                  maxHeight: 120,
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                {this.state.error.message}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default withTranslation()(ErrorBoundary);
